// Item-level charges UI (2026-05-26). Operator-locked: charges should
// not require an imprint location — that's the PromoStandards /
// commonsku pattern we explicitly don't follow. This section sits on
// every presentation item directly, with `+ Setup` / `+ Run` /
// `+ Fixed` buttons that create entries in the new
// `presentation_item_charges` table.
//
// Same shape as the decoration charges UI (PR #129) — tri-field cost/
// sell/margin auto-compute, per-charge display_to_client toggle.
// Shares helpers (PriceTriField, NumberField, suggestLabel,
// CHARGE_TYPE_LABELS, extractError) with PresentationDecorationSection
// so the visual + behavior stays consistent.

import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Calculator, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { LiveCell } from "@/components/product-edit/LiveCell";
import { Input } from "@/components/ui/Input";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  CHARGE_TYPE_LABELS,
  NumberField,
  PriceTriField,
  extractError,
  suggestLabel,
  type ChargeType,
} from "./PresentationDecorationSection";
import { DecorationRateCardPanel } from "./PresentationItemDecorationPricing";

/** Charge types grouped under "Decoration" in the item's charges. */
const DECORATION_TYPES: ReadonlySet<ChargeType> = new Set<ChargeType>(["setup", "run"]);

export interface ItemCharge {
  id: string;
  presentationItemId: string;
  position: number;
  chargeType: ChargeType;
  label: string;
  quantity: number;
  cost: string;
  sellPrice: string;
  marginPct: string | null;
  displayToClient: boolean;
}

interface Props {
  itemId: string;
  /** Largest tier quantity on the parent item — used as the default qty
   *  for new "run" charges (per-piece). Falls back to 1 when the item
   *  has no tiers. */
  itemMaxTierQty: number;
  /** Every tier quantity on the item, ascending. Drives "Price from rate
   *  card", which prices decoration AT a tier quantity rather than at a
   *  guessed one. Optional so callers that predate the rate-card panel keep
   *  working — an empty list makes the panel say what is missing. */
  itemTierQuantities?: number[];
  charges: ItemCharge[];
  canEdit: boolean;
  onMutated: () => void;
}

export function ItemChargesSection(props: Props) {
  const {
    itemId,
    itemMaxTierQty,
    itemTierQuantities,
    charges,
    canEdit,
    onMutated,
  } = props;
  const [addingType, setAddingType] = useState<ChargeType | null>(null);
  const [pricingFromRateCard, setPricingFromRateCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** The two "add a charge" entry points are mutually exclusive — opening one
   *  closes the other. */
  function openManual(type: ChargeType): void {
    setPricingFromRateCard(false);
    setAddingType(type);
  }
  function openRateCard(): void {
    setAddingType(null);
    setPricingFromRateCard(true);
  }

  const remove = useMutation({
    mutationFn: async (chargeId: string) =>
      api(`/presentation_item_charges/${chargeId}`, {
        method: "DELETE",
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  // 🔴 LIVE CELL SAVES. CEO 2026-08-18: "You need to be able to update the
  // setup and run charges - you can't edit it now." They WERE editable — behind
  // an unlabelled 14px pencil at the end of the row, which is
  // indistinguishable from not editable. Label, quantity, cost and sell are now
  // typed straight into the table, matching the pricing grid.
  const patchField = useMutation({
    mutationFn: async (args: { chargeId: string; body: Record<string, unknown> }) =>
      api(`/presentation_item_charges/${args.chargeId}`, {
        method: "PATCH",
        body: JSON.stringify(args.body),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => { setError(null); onMutated(); },
  });

  const toggleVisible = useMutation({
    mutationFn: async (args: { chargeId: string; visible: boolean }) =>
      api(`/presentation_item_charges/${args.chargeId}`, {
        method: "PATCH",
        body: JSON.stringify({ display_to_client: args.visible }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  const sorted = useMemo(
    () =>
      [...charges].sort(
        (a, b) => a.position - b.position || a.label.localeCompare(b.label),
      ),
    [charges],
  );

  const decorationCharges = sorted.filter((c) => DECORATION_TYPES.has(c.chargeType));
  const otherCharges = sorted.filter((c) => !DECORATION_TYPES.has(c.chargeType));

  const chargeTable = (rows: ItemCharge[]) => (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
              <tr>
                <th className="py-1 pr-2">Type</th>
                <th className="py-1 pr-2">Label</th>
                <th className="py-1 pr-2">Qty</th>
                <th className="py-1 pr-2">Cost</th>
                <th className="py-1 pr-2">Sell</th>
                <th className="py-1 pr-2">Margin</th>
                <th className="py-1 pr-2">Total sell</th>
                <th className="py-1 pr-2">Visible</th>
                {canEdit && <th className="py-1 pr-1 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                  <tr key={c.id} className="border-t border-brown-50/60">
                    <td className="py-1.5 pr-2">
                      <span className="rounded-full bg-peach-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500">
                        {CHARGE_TYPE_LABELS[c.chargeType] ?? c.chargeType}
                      </span>
                    </td>
                    <td className="w-44 py-1.5 pr-2">
                      <LiveCell
                        value={c.label}
                        align="left"
                        ariaLabel={`Label for the ${CHARGE_TYPE_LABELS[c.chargeType] ?? c.chargeType} charge`}
                        disabled={!canEdit}
                        validate={(raw) =>
                          raw === "" ? "Label cannot be empty." : null
                        }
                        onCommit={(raw) =>
                          patchField.mutate({ chargeId: c.id, body: { label: raw } })
                        }
                      />
                    </td>
                    <td className="w-20 py-1.5 pr-2">
                      <LiveCell
                        value={String(c.quantity)}
                        ariaLabel={`Quantity for ${c.label}`}
                        disabled={!canEdit}
                        validate={(raw) => {
                          const q = Number.parseInt(raw, 10);
                          return Number.isInteger(q) && q >= 0
                            ? null
                            : "Quantity must be a non-negative whole number.";
                        }}
                        onCommit={(raw) =>
                          patchField.mutate({
                            chargeId: c.id,
                            body: { quantity: Number.parseInt(raw, 10) },
                          })
                        }
                      />
                    </td>
                    <td className="w-24 py-1.5 pr-2">
                      <LiveCell
                        value={c.cost}
                        ariaLabel={`Cost for ${c.label}`}
                        disabled={!canEdit}
                        validate={(raw) =>
                          /^\d+(\.\d{1,3})?$/.test(raw)
                            ? null
                            : "Cost must be numeric (e.g. 12.00 or 1.692)."
                        }
                        onCommit={(raw) =>
                          patchField.mutate({ chargeId: c.id, body: { cost: raw } })
                        }
                      />
                    </td>
                    <td className="w-24 py-1.5 pr-2">
                      <LiveCell
                        value={c.sellPrice}
                        ariaLabel={`Sell price for ${c.label}`}
                        disabled={!canEdit}
                        validate={(raw) =>
                          /^\d+(\.\d{1,2})?$/.test(raw)
                            ? null
                            : "Sell must be numeric (e.g. 20.00)."
                        }
                        onCommit={(raw) =>
                          patchField.mutate({ chargeId: c.id, body: { sell_price: raw } })
                        }
                      />
                    </td>
                    <td className="py-1.5 pr-2 text-brown-400">
                      {c.marginPct
                        ? `${Number(c.marginPct).toFixed(1)}%`
                        : "—"}
                    </td>
                    <td className="py-1.5 pr-2 font-semibold text-rust-500">
                      ${(Number(c.sellPrice) * c.quantity).toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-2">
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() =>
                            toggleVisible.mutate({
                              chargeId: c.id,
                              visible: !c.displayToClient,
                            })
                          }
                          disabled={toggleVisible.isPending}
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                            c.displayToClient
                              ? "bg-peach-100 text-brown-500 hover:bg-peach-200"
                              : "border border-rust-300 text-rust-500 hover:bg-rust-50",
                          )}
                        >
                          {c.displayToClient ? "Visible" : "Internal only"}
                        </button>
                      ) : (
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                            c.displayToClient
                              ? "bg-peach-100 text-brown-500"
                              : "border border-rust-300 text-rust-500",
                          )}
                        >
                          {c.displayToClient ? "Visible" : "Internal only"}
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="py-1.5 pr-1">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => remove.mutate(c.id)}
                            disabled={remove.isPending}
                            className="rounded-md p-1 text-brown-300 hover:bg-rust-50 hover:text-rust-500"
                            aria-label="Remove charge"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
  );

  return (
    <div className="border-t border-brown-50 px-3 py-3">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          Charges
        </p>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Listed FIRST and styled as the primary action: for apparel it
                replaces all three of the manual buttons, and typing a
                decoration rate by hand is the thing this section exists to
                stop. The manual buttons stay for everything the rate card
                does not cover (rush fees, freight, one-offs). */}
            {/* Peach, one step stronger than the outline `AddButton`s beside
                it — rust is the app's error/blocking token (and is used for
                exactly that inside this panel), so it must not read as a CTA. */}
            <button
              type="button"
              onClick={openRateCard}
              className="inline-flex items-center gap-1 rounded-md border border-peach-400 bg-peach-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-600 hover:bg-peach-300"
            >
              <Calculator className="h-3 w-3" />
              Price from rate card
            </button>
            {/* Each entry point closes the other: two unrelated open forms
                stacked under the table is not a state a rep should reach. */}
            <AddButton type="setup" onClick={() => openManual("setup")} />
            <AddButton type="run" onClick={() => openManual("run")} />
            <AddButton type="other" label="+ Fixed" onClick={() => openManual("other")} />
          </div>
        )}
      </div>
      {error && <p className="mb-2 text-xs text-rust-600">{error}</p>}
      {sorted.length === 0 && addingType === null ? (
        <p className="text-xs italic text-brown-300">
          No charges yet — Setup (one-time, per-color flat), Run (per-piece), Fixed (digitizing, rush, freight, etc.).
        </p>
      ) : (
        <div className="space-y-3">
          {/* Setup + run together under "Decoration"; everything else under
              "Other charges" (Stephanie 2026-10-12). Same table, same edits. */}
          {decorationCharges.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold text-brown-500">Decoration</p>
              {chargeTable(decorationCharges)}
            </div>
          )}
          {otherCharges.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold text-brown-500">Other charges</p>
              {chargeTable(otherCharges)}
            </div>
          )}
        </div>
      )}
      {pricingFromRateCard && (
        <DecorationRateCardPanel
          itemId={itemId}
          tierQuantities={itemTierQuantities ?? []}
          hasExistingCharges={charges.length > 0}
          onCancel={() => setPricingFromRateCard(false)}
          onApplied={() => {
            setPricingFromRateCard(false);
            onMutated();
          }}
        />
      )}
      {addingType !== null && (
        <AddRow
          itemId={itemId}
          initialType={addingType}
          itemMaxTierQty={itemMaxTierQty}
          onCancel={() => setAddingType(null)}
          onAdded={() => {
            setAddingType(null);
            onMutated();
          }}
        />
      )}
    </div>
  );
}

function AddButton(props: {
  type: ChargeType;
  onClick: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className="inline-flex items-center gap-1 rounded-md border border-peach-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500 hover:bg-peach-50"
    >
      <Plus className="h-3 w-3" />
      {props.label ?? CHARGE_TYPE_LABELS[props.type]}
    </button>
  );
}

function AddRow(props: {
  itemId: string;
  initialType: ChargeType;
  itemMaxTierQty: number;
  onCancel: () => void;
  onAdded: () => void;
}) {
  const { itemId, initialType, itemMaxTierQty, onCancel, onAdded } = props;
  const defaultQty = initialType === "run" ? Math.max(itemMaxTierQty, 1) : 1;
  const [chargeType, setChargeType] = useState<ChargeType>(initialType);
  const [label, setLabel] = useState(suggestLabel(initialType));
  const [quantity, setQuantity] = useState(String(defaultQty));
  const [cost, setCost] = useState("");
  const [sell, setSell] = useState("");
  const [margin, setMargin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      api(`/presentation_items/${itemId}/charges`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => onAdded(),
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    const q = Number.parseInt(quantity, 10);
    if (!Number.isInteger(q) || q < 0) {
      setError("Quantity must be a non-negative whole number.");
      return;
    }
    const c = cost.trim();
    const s = sell.trim();
    if (!/^\d+(\.\d{1,3})?$/.test(c)) {
      setError("Cost must be numeric (e.g. 12.00 or 1.692).");
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(s)) {
      setError("Sell must be numeric (e.g. 20.00).");
      return;
    }
    if (label.trim() === "") {
      setError("Add a short label (e.g. 'Rush fee').");
      return;
    }
    create.mutate({
      charge_type: chargeType,
      label: label.trim(),
      quantity: q,
      cost: c,
      sell_price: s,
      display_to_client: true,
    });
  };

  return (
    <form
      onSubmit={submit}
      className="mt-3 rounded-md border border-peach-300/70 bg-white p-3"
    >
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <label className="block md:col-span-1">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Type
          </span>
          <select
            value={chargeType}
            onChange={(e) => {
              const next = e.target.value as ChargeType;
              setChargeType(next);
              setLabel(suggestLabel(next));
              if (next === "run") {
                setQuantity(String(Math.max(itemMaxTierQty, 1)));
              } else {
                setQuantity("1");
              }
            }}
            className="h-8 w-full rounded-md border border-brown-100 bg-white px-2 text-xs text-brown-500 focus:border-peach-400 focus:outline-none"
          >
            {(
              [
                "setup",
                "run",
                "digitizing",
                "rush",
                "screen",
                "pms_match",
                "other",
              ] as ChargeType[]
            ).map((t) => (
              <option key={t} value={t}>
                {CHARGE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="block md:col-span-2">
          <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-brown-300">
            Label
          </span>
          <Input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={200}
            placeholder="e.g. Rush fee"
            className="h-8 px-2 text-xs"
          />
        </label>
        <NumberField
          label="Qty"
          value={quantity}
          onChange={setQuantity}
          placeholder="1"
        />
        <PriceTriField
          cost={cost}
          sell={sell}
          margin={margin}
          onCost={setCost}
          onSell={setSell}
          onMargin={setMargin}
        />
      </div>
      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={create.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add charge"}
        </Button>
      </div>
    </form>
  );
}

// EditRow was removed 2026-08-18. Label, quantity, cost and sell are typed
// straight into the table now (LiveCell), so there is no open-edit-save cycle
// left for it to serve. Its validation moved into the per-cell `validate`
// unchanged: label non-empty, quantity a non-negative integer, cost 3dp,
// sell 2dp.
